'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SocialPost {
  id: number;
  platform: string;
  content: string;
  engagement: string;
  timestamp: string;
  icon: string;
}

interface CampaignTemplate {
  id: number;
  title: string;
  description: string;
  platforms: string[];
  downloadUrl: string;
}

const SocialMediaSection: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const socialPosts: SocialPost[] = [
    {
      id: 1,
      platform: 'Twitter',
      content: 'Join 50 million people worldwide choosing compassion over hostility this April 17th. #SIGDay2026 #NoEnemyFestival',
      engagement: '2.4M impressions',
      timestamp: '2 hours ago',
      icon: 'ChatBubbleLeftIcon',
    },
    {
      id: 2,
      platform: 'Instagram',
      content: 'Stories of transformation from communities across 180 countries. Swipe to see how forgiveness is changing lives. 🕊️',
      engagement: '850K likes',
      timestamp: '5 hours ago',
      icon: 'PhotoIcon',
    },
    {
      id: 3,
      platform: 'LinkedIn',
      content: 'New research shows 73% reduction in community conflicts in SIG Day (The Global No Enemy Festival) participating regions. Read the full study.',
      engagement: '125K engagements',
      timestamp: '1 day ago',
      icon: 'BriefcaseIcon',
    },
    {
      id: 4,
      platform: 'Facebook',
      content: 'Live now: Dr. Sarah Mitchell discusses the psychology of forgiveness and its impact on global peace. Join the conversation!',
      engagement: '1.2M views',
      timestamp: '3 hours ago',
      icon: 'VideoCameraIcon',
    },
  ];

  const campaignTemplates: CampaignTemplate[] = [
    {
      id: 1,
      title: 'Social Media Graphics Pack',
      description: 'Ready-to-post images for Instagram, Facebook, Twitter, and LinkedIn',
      platforms: ['Instagram', 'Facebook', 'Twitter', 'LinkedIn'],
      downloadUrl: '#',
    },
    {
      id: 2,
      title: 'Video Story Templates',
      description: 'Editable video templates for Instagram Stories and Reels',
      platforms: ['Instagram', 'TikTok', 'YouTube'],
      downloadUrl: '#',
    },
    {
      id: 3,
      title: 'Hashtag Campaign Kit',
      description: 'Curated hashtags, captions, and posting schedules',
      platforms: ['All Platforms'],
      downloadUrl: '#',
    },
  ];

  const trendingHashtags = [
    { tag: '#SIGDay2026', posts: '2.4M' },
    { tag: '#NoEnemyFestival', posts: '1.8M' },
    { tag: '#ChooseCompassion', posts: '950K' },
    { tag: '#GlobalPeaceMovement', posts: '720K' },
    { tag: '#ForgivenessHeals', posts: '580K' },
  ];

  if (!isHydrated) {
    return (
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-1/3 mx-auto" />
            <div className="h-64 bg-muted rounded" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-6 py-3 rounded-full mb-6">
            <Icon name="HashtagIcon" size={24} className="text-primary" />
            <span className="text-primary font-cta text-sm font-bold">
              Social Media Hub
            </span>
          </div>

          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-6">
            Join the Global Conversation
          </h2>

          <p className="text-lg text-muted-foreground max-w-3xl mx-auto font-body">
            Access campaign materials, track trending content, and amplify the message of peace across all social platforms.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-headline font-bold text-foreground mb-6">
              Recent Posts
            </h3>

            <div className="space-y-4">
              {socialPosts.map((post) => (
                <div
                  key={post.id}
                  className="bg-card rounded-lg p-6 border border-border transition-all duration-300 hover:shadow-lg hover:border-primary"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Icon name={post.icon as any} size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-cta text-sm font-bold text-foreground">
                          {post.platform}
                        </p>
                        <p className="text-xs text-muted-foreground font-body">
                          {post.timestamp}
                        </p>
                      </div>
                    </div>

                    <button className="text-muted-foreground hover:text-primary transition-colors duration-300">
                      <Icon name="ShareIcon" size={20} />
                    </button>
                  </div>

                  <p className="text-foreground font-body leading-relaxed mb-4">
                    {post.content}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <span className="text-sm text-muted-foreground font-body">
                      {post.engagement}
                    </span>

                    <button className="flex items-center space-x-2 text-primary font-cta text-sm font-bold transition-colors duration-300 hover:text-peaceful-teal">
                      <span>View Post</span>
                      <Icon name="ArrowTopRightOnSquareIcon" size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-headline font-bold text-foreground mb-6">
              Trending Hashtags
            </h3>

            <div className="bg-card rounded-lg p-6 border border-border mb-8">
              <div className="space-y-4">
                {trendingHashtags.map((hashtag, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between pb-4 border-b border-border last:border-0 last:pb-0"
                  >
                    <div>
                      <p className="font-cta text-base font-bold text-primary">
                        {hashtag.tag}
                      </p>
                      <p className="text-sm text-muted-foreground font-body">
                        {hashtag.posts} posts
                      </p>
                    </div>

                    <button className="text-muted-foreground hover:text-primary transition-colors duration-300">
                      <Icon name="ArrowTrendingUpIcon" size={20} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-secondary to-primary rounded-lg p-6 text-center">
              <Icon name="BellAlertIcon" size={40} className="text-white mx-auto mb-4" />
              <h4 className="text-xl font-headline font-bold text-white mb-2">
                Stay Updated
              </h4>
              <p className="text-sm text-white/90 mb-4 font-body">
                Get real-time alerts for trending content and viral campaigns
              </p>
              <button className="w-full px-6 py-3 bg-white text-primary rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105">
                Enable Notifications
              </button>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-headline font-bold text-foreground mb-6 text-center">
            Campaign Templates
          </h3>

          <div className="grid md:grid-cols-3 gap-6">
            {campaignTemplates.map((template) => (
              <div
                key={template.id}
                className="bg-card rounded-lg p-6 border border-border transition-all duration-300 hover:shadow-lg hover:border-primary group"
              >
                <div className="p-3 bg-primary/10 rounded-lg inline-block mb-4 group-hover:bg-primary/20 transition-colors duration-300">
                  <Icon name="SparklesIcon" size={28} className="text-primary" />
                </div>

                <h4 className="text-xl font-headline font-bold text-foreground mb-2">
                  {template.title}
                </h4>

                <p className="text-sm text-muted-foreground mb-4 font-body">
                  {template.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-4">
                  {template.platforms.map((platform, index) => (
                    <span
                      key={index}
                      className="text-xs font-body text-foreground bg-muted px-2 py-1 rounded"
                    >
                      {platform}
                    </span>
                  ))}
                </div>

                <button className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2">
                  <Icon name="ArrowDownTrayIcon" size={18} />
                  <span>Download Template</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialMediaSection;